import { useState, useEffect } from "react";
import { ButtonSiguiente, Input, Select } from "../../ui";
import { safeFetch } from "@/api/safeFetch";
import { useNavigate } from "react-router-dom";
import { buildImageUrl } from "@/api/buildImageUrl";

interface Seccion {
    id: number;
    nombreSeccion: string;
    descripcion: string;
    foto_perfil: string;
}

const EditarSecciones: React.FC = () => {
    const [secciones, setSecciones] = useState<Seccion[]>([]);
    const [selectedSeccionId, setSelectedSeccionId] = useState<number | null>(null);
    const [nombreSeccion, setNombreSeccion] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [foto, setFoto] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    // Cargar secciones
    useEffect(() => {
        const fetchSecciones = async () => {
            try {
                const res = await safeFetch("/api/seccion");
                if (!res.ok) throw new Error("Error cargando secciones");
                setSecciones(await res.json());
            } catch (err: any) {
                setError(err.message || "Error al cargar secciones");
            }
        };
        fetchSecciones();
    }, []);

    // Rellenar datos al seleccionar sección
    useEffect(() => {
        if (!selectedSeccionId) return;

        const seccion = secciones.find((s) => s.id === selectedSeccionId);
        if (!seccion) return;

        setNombreSeccion(seccion.nombreSeccion);
        setDescripcion(seccion.descripcion);
        setPreview(buildImageUrl(seccion.foto_perfil));
        setFoto(null);
        setError("");
        setSuccess("");
    }, [selectedSeccionId, secciones]);

    // Cambiar imagen
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;

        // liberar blob anterior
        if (preview && preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
        }

        setFoto(file);
        if (file) setPreview(URL.createObjectURL(file));
    };

    // Guardar cambios
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!selectedSeccionId || !nombreSeccion || !descripcion) {
            setError("Selecciona una sección y completa todos los campos");
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();
            formData.append("nombreSeccion", nombreSeccion);
            formData.append("descripcion", descripcion);

            // 🔥 IMPORTANTE: enviar imagen si existe
            if (foto) {
                formData.append("foto_perfil", foto);
            }

            const res = await safeFetch(`/api/editarSecciones/${selectedSeccionId}`, {
                method: "PUT",
                body: formData,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error("Error al actualizar la sección");

            const data = await res.json();

            setSuccess("Sección actualizada exitosamente 🎉");

            // 🔥 Si el backend devuelve nueva imagen, actualizar preview real
            if (data.imagen_url) {
                setPreview(buildImageUrl(data.imagen_url));
            } else if (foto) {
                // fallback: mostrar blob local
                setPreview(URL.createObjectURL(foto));
            }

            setFoto(null);
        } catch (err: any) {
            setError(err.message || "Ocurrió un error.");
        } finally {
            setLoading(false);
        }
    };

    // Limpiar inputs file al desmontar
    useEffect(() => {
        return () => {
            const inputs = document.querySelectorAll<HTMLInputElement>('input[type="file"]');
            inputs.forEach((input) => (input.value = ""));
        };
    }, []);

    // Liberar blob al desmontar
    useEffect(() => {
        return () => {
            if (preview && preview.startsWith("blob:")) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);


    return (
        <div className="w-full min-h-screen flex items-center justify-center relative bg-zinc-500 p-4">

            {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4 z-10">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            {/* Contenedor principal */}
            <div className="w-full h-full dark:text-zinc-700 max-w-4xl p-6 bg-zinc-400 shadow-md shadow-green-700 rounded-xl flex flex-col gap-6 border-zinc-600 border-r-4 border-t-2">

                <h2 className="text-2xl font-bold text-center mb-2">Editar Secciones</h2>

                {error && <p className="text-red-600">{error}</p>}
                {success && <p className="text-green-600">{success}</p>}

                <form onSubmit={handleSubmit} className="w-full flex flex-col gap-6 h-full">

                    <div className="flex flex-col md:flex-row gap-6 w-full h-full">

                        {/* Columna izquierda: Select + Input + Textarea */}
                        <div className="flex-1 flex flex-col gap-4">
                            {/* Selector */}
                            <div>
                                <label className="font-medium block mb-1">Selecciona la Sección</label>
                                <Select
                                    value={selectedSeccionId ?? ""}
                                    onChange={(e) => setSelectedSeccionId(Number(e.target.value))}
                                    options={[
                                        { value: "", label: "Secciones" }, // 👈 opción predeterminada

                                        ...[...secciones]
                                            .sort((a, b) => a.nombreSeccion.localeCompare(b.nombreSeccion))
                                            .map((s) => ({
                                                value: s.id,
                                                label: s.nombreSeccion
                                            }))
                                    ]}
                                    className="w-full border p-2 rounded dark:bg-white"
                                />

                            </div>

                            {/* Nombre */}
                            <div>
                                <label className="font-medium">Nombre de la Sección</label>
                                <Input
                                    type="text"
                                    className="w-full border p-2 rounded"
                                    value={nombreSeccion}
                                    onChange={(e) => setNombreSeccion(e.target.value)}
                                />
                            </div>

                            {/* Textarea */}
                            <div className="flex-1 flex flex-col">
                                <label className="font-medium">Descripción</label>
                                <textarea
                                    className="w-full border p-2 rounded flex-1 resize-none overflow-auto dark:bg-white"
                                    value={descripcion}
                                    onChange={(e) => setDescripcion(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Columna derecha: Imagen */}
                        <div className="flex-1 flex flex-col items-center justify-start gap-6">
                            <label className="font-medium">Imagen de la Sección</label>

                            {/* Botón de selección de imagen + nombre al lado */}
                            <div className="flex flex-col items-center w-full gap-2">
                                <label className="flex items-center justify-center py-2 px-6 bg-zinc-600 rounded-md border border-white text-white cursor-pointer hover:text-zinc-800 hover:bg-gray-100 hover:border-blue-700 transition">
                                    <span className="font-semibold">Seleccionar Imagen</span>
                                    <input
                                        type="file"
                                        name="foto_perfil"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleImageChange}
                                    />

                                </label>

                                {/* Mostrar nombre del archivo debajo, centrado */}
                                {foto && (
                                    <span className="text-sm text-gray-600 truncate max-w-[70%] text-center">
                                        {foto.name}
                                    </span>
                                )}
                            </div>

                            {/* Preview debajo */}
                            {preview && (
                                <img
                                    src={preview}
                                    className="w-48 h-48 object-cover mt-4 rounded-xl border-2 border-zinc-600"
                                    alt="Preview"
                                />
                            )}
                        </div>


                    </div>

                    {/* Botón Guardar debajo de las dos columnas */}
                    <div className="w-full">
                        <ButtonSiguiente
                            type="submit"
                            disabled={loading}
                            className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition w-full mt-4"
                        >
                            {loading ? "Guardando..." : "Actualizar Sección"}
                        </ButtonSiguiente>
                    </div>

                </form>

            </div>
        </div>
    );


};

export default EditarSecciones;
